import { useDispatch } from "react-redux";

import useShallowSelector from "./useShalowSelector";
import {
    IUser,
    ISignInRequestData,
    ISignUpRequestData,
    IUserTypes,
    IForgotPasswordRequestData,
} from "../store/auth/types";
import {
    selectAuthUser,
    selectAuthLoading,
    selectAuthUserType,
    selectAuthErrors,
    selectIsOAuth,
    selectRedirectUrlQueries,
} from "../store/auth/selectors";
import { forgotPasswordRequest, oauthFlowRequest, signInRequest, signUpRequest } from "../store/auth/actionCreators";
import { selectUIUserOptions } from "../store/ui/selectors";
import { IError } from "../store/types/errors";
import { changeUserOptions } from "../store/ui/actionCreators";

export interface onSignIn {
    (signInRequestData: ISignInRequestData): void;
}
export interface onSignUp {
    (signUpRequestData: ISignUpRequestData): void;
}

export interface onForgotPassword {
    (forgotPasswordRequestData: IForgotPasswordRequestData): void;
}

export interface onToggle {
    (): void;
}

type UseUser = () => {
    user: IUser | null;
    errors: IError | null;
    userType: IUserTypes;
    isLoading: boolean;
    isOpen: boolean;
    onSignIn: onSignIn;
    onSignUp: onSignUp;
    onToggle: onToggle;
    onForgotPassword: onForgotPassword;
};

const useUser: UseUser = () => {
    const dispatch = useDispatch();

    const user = useShallowSelector(selectAuthUser);
    const userType = useShallowSelector(selectAuthUserType);
    const errors = useShallowSelector(selectAuthErrors);
    const isLoading = useShallowSelector(selectAuthLoading);
    const { isOpen } = useShallowSelector(selectUIUserOptions);
    const urlQueries = useShallowSelector(selectRedirectUrlQueries);
    const isOAuth = useShallowSelector(selectIsOAuth);

    const onSignIn: onSignIn = signInRequestData => {
        if (isOAuth && urlQueries) {
            const urlParams = new URLSearchParams(urlQueries);

            const client_id = urlParams.get("client_id")!;
            const redirect_url = urlParams.get("redirect_uri")!;
            const state = urlParams.get("state")!;
            const scope = urlParams.get("scope")!;
            dispatch(
                oauthFlowRequest({
                    client_id,
                    redirect_url,
                    email: signInRequestData.email,
                    password: signInRequestData.password,
                    state,
                    scope,
                }),
            );
            return;
        }
        dispatch(signInRequest(signInRequestData));
    };

    const onSignUp: onSignUp = signUpRequestData => {
        dispatch(signUpRequest(signUpRequestData));
    };

    const onForgotPassword: onForgotPassword = forgotPasswordRequestData => {
        dispatch(forgotPasswordRequest(forgotPasswordRequestData));
    };

    const onToggle: onToggle = () => {
        dispatch(changeUserOptions({ isOpen: !isOpen }));
    };

    return {
        user,
        errors,
        userType,
        isLoading,
        isOpen,
        onSignIn,
        onSignUp,
        onToggle,
        onForgotPassword,
    };
};
export default useUser;
